import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';

import ConfidenceChip from './ConfidenceChip';
import AiReviewFindings, { IAiReviewFinding } from './AiReviewFindings';
import AiPromptVersionLink from './AiPromptVersionLink';

export const AI_REVIEW_RUN_QUERY = gql`
  query aiReviewRun($id: ID!) {
    aiReviewRun(id: $id) {
      id
      requestId
      attempt
      status
      provider
      model
      startedAt
      finishedAt
      durationMs
      userName
      userRoles
      producerId
      isStoreProduct
      trigger
      category
      options
      prompt
      error
      request
      input
      storedProduct
      output
      stats
      price
      estimatedCost {
        inputUsd
        cachedInputUsd
        outputUsd
        webSearchUsd
        totalUsd
        notes
      }
      response
      responseHeaders
      attempts {
        id
        attempt
        status
        model
        startedAt
        errorMessage
      }
    }
  }
`;

export const AI_REVIEW_RUNS_QUERY = gql`
  query aiReviewRuns($productId: ID!) {
    aiReviewRuns(productId: $productId) {
      id
      requestId
      attempt
      status
      provider
      model
      startedAt
      durationMs
      trigger
      userName
      promptVersion
      errorStage
      errorMessage
      fieldCount
      failedCount
      estimatedCostUsd
    }
  }
`;

export interface IAiReviewRunListItem {
  id: string;
  requestId: string;
  attempt: number;
  status: string;
  provider: string;
  model: string;
  startedAt: string;
  durationMs: number;
  trigger: string | null;
  userName: string | null;
  // In the Statistics list only.
  productId?: string | null;
  productTitle?: string | null;
  producer?: { id: string; name: string | null; username: string | null; isStore: boolean; organizations: string[] } | null;
  brand?: string | null;
  promptVersion: number | null;
  errorStage: string | null;
  errorMessage: string | null;
  fieldCount: number;
  failedCount: number;
  estimatedCostUsd: number | null;
}

// One line per run, as the history lists show it.
export const describeRunOutcome = (run: IAiReviewRunListItem) =>
  run.status === 'error' ? `failed at ${run.errorStage || 'unknown'}` : `${run.failedCount} failed of ${run.fieldCount}`;

type Json = Record<string, any>;

interface IAiReviewRunCost {
  inputUsd: number;
  cachedInputUsd: number;
  outputUsd: number;
  webSearchUsd: number;
  totalUsd: number;
  notes: string[];
}

interface IFieldVerdict {
  field: string;
  fieldLabel: string;
  status: string;
  confidence?: number | null;
  reason?: string;
  findings?: IAiReviewFinding[] | null;
}

interface IAiReviewRunAttempt {
  id: string;
  attempt: number;
  status: string;
  model: string;
  startedAt: string;
  errorMessage: string | null;
}

export interface IAiReviewRun {
  id: string;
  requestId: string;
  attempt: number;
  status: string;
  provider: string;
  model: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  userName: string | null;
  userRoles: string[];
  producerId: string | null;
  isStoreProduct: boolean | null;
  trigger: string | null;
  category: Json | null;
  options: Json | null;
  prompt: Json | null;
  error: Json | null;
  request: Json | null;
  input: Json | null;
  storedProduct: Json | null;
  output: Json | null;
  stats: Json | null;
  price: Json | null;
  estimatedCost: IAiReviewRunCost | null;
  response: Json | null;
  responseHeaders: Json | null;
  attempts: IAiReviewRunAttempt[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    facts: {
      display: 'grid',
      gridTemplateColumns: 'minmax(160px, max-content) 1fr',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(0.5),
      margin: 0,
      '& dt': { color: theme.palette.text.secondary },
      '& dd': { margin: 0, overflowWrap: 'anywhere' },
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
    },
    text: {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      fontSize: '0.875rem',
      margin: 0,
    },
    code: {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      maxHeight: 400,
      overflowY: 'auto',
      width: '100%',
      margin: 0,
    },
    verdict: {
      padding: theme.spacing(1, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 'none' },
    },
    verdictHeader: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    cost: {
      fontWeight: 600,
    },
    list: {
      margin: 0,
      paddingLeft: theme.spacing(2.5),
      overflowWrap: 'anywhere',
    },
  })
);

const formatTime = (value: string) => new Date(value).toLocaleString('sv-SE');
const formatNumber = (value: unknown) => (typeof value === 'number' ? value.toLocaleString('sv-SE') : '—');
const formatUsd = (value: unknown) =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 3 })
    : '—';
const stripOpenAiTracking = (url: string) => url.replace(/([?&])utm_source=openai(&|$)/, (_m, sep, end) => (end ? sep : ''));

const TRIGGER_LABELS: Record<string, string> = {
  auto: 'Opening the AI panel',
  button: 'Review button',
};

// Root first; the synthetic "Root" node adds nothing to read.
const categoryPath = (category: Json | null) => {
  const path: { id: string; name: string | null }[] = category?.path || [];
  const names = path.filter(node => node.id !== 'root').map(node => node.name || node.id);
  return names.length ? names.join(' › ') : category?.name || '—';
};

const formatCost = (value: number) => `$${value.toFixed(value < 0.1 ? 4 : 3)}`;
const shortHash = (value: unknown) => (typeof value === 'string' && value ? value.slice(0, 12) : '—');
const yesNo = (value: boolean | null) => (value === null ? '—' : value ? 'Yes' : 'No');

// The URL overrides that chose the Gemini model; OpenAI takes none.
const describeOptions = (run: IAiReviewRun) => {
  if (run.provider !== 'gemini' || !run.options) return 'None';
  const parts = [
    run.options.useFallback ? 'fallback model' : null,
    run.options.useFlash ? 'flash' : null,
    typeof run.options.temperature === 'number' ? `temperature ${run.options.temperature}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'None';
};

const Facts = ({ rows }: { rows: [string, React.ReactNode][] }) => {
  const classes = useStyles();
  return (
    <dl className={classes.facts}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const Raw = ({ title, value }: { title: string; value: unknown }) => {
  const classes = useStyles();
  if (value === null || value === undefined || value === '') return null;
  return (
    <Accordion TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <pre className={classes.code}>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
      </AccordionDetails>
    </Accordion>
  );
};

const FieldVerdicts = ({ verdicts, provider }: { verdicts: IFieldVerdict[]; provider: string }) => {
  const classes = useStyles();
  // Failed fields first, then the least confident.
  const sorted = [...verdicts].sort(
    (a, b) =>
      Number(b.status === 'failed') - Number(a.status === 'failed') || (a.confidence ?? 101) - (b.confidence ?? 101)
  );
  return (
    <Accordion TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">
          Field verdicts ({verdicts.length}, {verdicts.filter(verdict => verdict.status === 'failed').length} failed)
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box width="100%" data-testid="ai-review-run-verdicts">
          {sorted.map(verdict => (
            <div key={verdict.field} className={classes.verdict}>
              <div className={classes.verdictHeader}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {verdict.fieldLabel}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {verdict.field}
                </Typography>
                <Chip size="small" color={verdict.status === 'failed' ? 'secondary' : 'default'} label={verdict.status} />
                {typeof verdict.confidence === 'number' && (
                  <ConfidenceChip confidence={verdict.confidence} provider={provider} />
                )}
                <AiReviewFindings findings={verdict.findings} reason={verdict.reason} />
              </div>
              {verdict.reason && <p className={classes.text}>{verdict.reason}</p>}
            </div>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const AiReviewRunDetails = ({ run }: { run: IAiReviewRun }) => {
  const classes = useStyles();
  const verdicts: IFieldVerdict[] = run.output?.parsed?.fieldReviews || [];
  const cost = run.estimatedCost;
  const stats = run.stats || {};
  const price = run.price;
  const queries: string[] = stats.webSearchQueries || [];
  const sources: { url: string; title: string | null }[] = stats.sources || [];
  const aiFilledFields: { field: string; model: string | null }[] = run.storedProduct?.aiFilledFields || [];
  const providerRequestId = run.responseHeaders?.['x-request-id'] || run.error?.providerRequestId;

  return (
    <>
      {run.status === 'error' && run.error && (
        <Box className={classes.section}>
          <Alert severity="error">
            Failed at {run.error.stage}: {run.error.message}
          </Alert>
        </Box>
      )}

      <Box className={classes.section}>
        <Facts
          rows={[
            ['Status', <Chip key="status" size="small" color={run.status === 'success' ? 'primary' : 'secondary'} label={run.status} />],
            ['Model', `${run.provider} · ${run.model}${stats.modelVersion && stats.modelVersion !== run.model ? ` (${stats.modelVersion})` : ''}`],
            ['Started', formatTime(run.startedAt)],
            ['Finished', run.finishedAt ? formatTime(run.finishedAt) : '—'],
            ['Duration', `${(run.durationMs / 1000).toFixed(1)} s`],
            ['Run by', `${run.userName || '—'}${run.userRoles.length ? ` (${run.userRoles.join(', ')})` : ''}`],
            ['Started from', TRIGGER_LABELS[run.trigger || ''] || '—'],
            ['Category', categoryPath(run.category)],
            [
              'Prompt',
              run.prompt ? (
                <span key="prompt">
                  <AiPromptVersionLink version={run.prompt.version}>Version {run.prompt.version}</AiPromptVersionLink>
                  {` · ${String(run.prompt.sha256).slice(0, 12)}`}
                </span>
              ) : (
                '—'
              ),
            ],
            ['Attempt', `${run.attempt} of ${run.attempts.length || 1}`],
            ['Request id', run.requestId],
            ['Overrides', describeOptions(run)],
            ['Producer', run.producerId || '—'],
            ['Store product', yesNo(run.isStoreProduct)],
          ]}
        />
      </Box>

      {run.attempts.length > 1 && (
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Attempts of this review
          </Typography>
          <ul className={classes.list}>
            {run.attempts.map(attempt => (
              <li key={attempt.id}>
                <Typography variant="body2">
                  {attempt.attempt}. {attempt.model} · {attempt.status}
                  {attempt.id === run.id ? ' (this run)' : ''}
                  {attempt.errorMessage ? ` · ${attempt.errorMessage}` : ''}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      <Box className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Usage
        </Typography>
        <Facts
          rows={[
            ['Input tokens', `${formatNumber(stats.inputTokens)}${typeof stats.cachedInputTokens === 'number' ? ` (${formatNumber(stats.cachedInputTokens)} cached)` : ''}`],
            ['Output tokens', `${formatNumber(stats.outputTokens)}${typeof stats.reasoningTokens === 'number' ? ` (${formatNumber(stats.reasoningTokens)} reasoning)` : ''}`],
            ...(typeof stats.toolUseInputTokens === 'number'
              ? [['Search result tokens', formatNumber(stats.toolUseInputTokens)] as [string, React.ReactNode]]
              : []),
            ['Total tokens', formatNumber(stats.totalTokens)],
            ['Web searches', formatNumber(stats.webSearchCalls)],
            ['Finish reason', stats.finishReason || '—'],
            ['Service tier', stats.serviceTier || '—'],
            ['Response id', stats.responseId || '—'],
            ['Provider request id', providerRequestId || '—'],
          ]}
        />
      </Box>

      {cost && (
        <Box className={classes.section} data-testid="ai-review-run-cost">
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Estimated cost
          </Typography>
          <Typography variant="body2" className={classes.cost}>
            {formatCost(cost.totalUsd)}
          </Typography>
          <Typography variant="body2">
            {formatCost(cost.inputUsd)} input · {formatCost(cost.cachedInputUsd)} cached input · {formatCost(cost.outputUsd)}{' '}
            output · {formatCost(cost.webSearchUsd)} web search
          </Typography>
          {cost.notes.map(note => (
            <Typography key={note} variant="caption" color="textSecondary" component="p">
              {note}
            </Typography>
          ))}
        </Box>
      )}

      <Box className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Input
        </Typography>
        <Facts
          rows={[
            ['Message sha256', shortHash(run.input?.sha256)],
            ['Product fields sha256', shortHash(run.input?.productSha256)],
            ['Fields sent', String(Object.keys(run.input?.product || {}).length)],
            ['Product versions then', typeof run.storedProduct?.versionCount === 'number' ? String(run.storedProduct.versionCount) : '—'],
            ['Last saved then', run.storedProduct?.lastVersionAt ? formatTime(new Date(run.storedProduct.lastVersionAt).toISOString()) : '—'],
          ]}
        />
        {run.input?.hints && (
          <Box mt={1.5} data-testid="ai-review-run-hints">
            {([
              ['Exceptions to check', run.input.hints.foodExceptions],
              ['Automatic plausibility checks', run.input.hints.nutritionSanity],
            ] as [string, string[]][]).map(([title, hints]) => (
              <Box key={title} mb={1}>
                <Typography variant="body2" color="textSecondary">
                  {title} the server added to the message
                </Typography>
                {hints?.length ? (
                  <ul className={classes.list}>
                    {hints.map(hint => (
                      <li key={hint}>
                        <Typography variant="body2">{hint}</Typography>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography variant="body2">None</Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {price && (
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Price when reviewed
          </Typography>
          <Typography variant="body2">
            {formatUsd(price.input)} input · {formatUsd(price.cachedInput)} cached input · {formatUsd(price.output)} output
            per 1M tokens (price table checked {price.checkedAt})
          </Typography>
          {price.note && (
            <Typography variant="body2" color="textSecondary">
              {price.note}
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            Web search: {price.webSearch}
          </Typography>
        </Box>
      )}

      {(queries.length > 0 || sources.length > 0) && (
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Web search ({formatNumber(stats.webSearchCalls)} billed {stats.webSearchCalls === 1 ? 'search' : 'searches'},{' '}
            {queries.length} {queries.length === 1 ? 'query' : 'queries'}, {sources.length} {sources.length === 1 ? 'source' : 'sources'})
          </Typography>
          <Typography variant="caption" color="textSecondary" component="p" gutterBottom>
            A billed search can send several queries at once; the price counts the searches.
          </Typography>
          {queries.length > 0 && (
            <ul className={classes.list} data-testid="ai-review-run-queries">
              {queries.map(query => (
                <li key={query}>
                  <Typography variant="body2">{query}</Typography>
                </li>
              ))}
            </ul>
          )}
          {sources.length > 0 && (
            <Accordion TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Sources ({sources.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className={classes.list}>
                  {sources.map(source => {
                    const url = stripOpenAiTracking(source.url);
                    return (
                      <li key={source.url}>
                        <Link variant="body2" href={url} target="_blank" rel="noopener noreferrer">
                          {source.title || url}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {run.output?.reasoningSummary && (
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Reasoning summary
          </Typography>
          <Typography variant="caption" color="textSecondary" component="p" gutterBottom>
            A summary by the provider, not the model&apos;s full reasoning.
          </Typography>
          <p className={classes.text} data-testid="ai-review-run-reasoning">
            {run.output.reasoningSummary}
          </p>
        </Box>
      )}

      {aiFilledFields.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Fields as filled by magic upload ({aiFilledFields.length})
          </Typography>
          <div className={classes.chips}>
            {aiFilledFields.map(field => (
              <Chip key={field.field} size="small" variant="outlined" label={field.field} title={field.model || undefined} />
            ))}
          </div>
        </Box>
      )}

      <Box className={classes.section}>
        {verdicts.length > 0 && <FieldVerdicts verdicts={verdicts} provider={run.provider} />}
        <Raw title="Request parameters" value={run.request} />
        <Raw title="Input sent to the model" value={run.input?.text} />
        <Raw title="Product fields sent" value={run.input?.product} />
        <Raw title="Raw output" value={run.output?.text} />
        <Raw title="Error details" value={run.error} />
        <Raw title="Full provider response" value={run.response} />
        <Raw title="Response headers" value={run.responseHeaders} />
      </Box>
    </>
  );
};

type TVerdictChange = {
  field: string;
  fieldLabel: string;
  before: IFieldVerdict | null;
  after: IFieldVerdict | null;
};

// How the verdicts of [run] differ from those of [base]: a changed status, or a field only one has.
export const compareVerdicts = (run: IAiReviewRun, base: IAiReviewRun) => {
  const verdictsOf = (item: IAiReviewRun): Map<string, IFieldVerdict> =>
    new Map((item.output?.parsed?.fieldReviews || []).map((verdict: IFieldVerdict) => [verdict.field, verdict]));
  const after = verdictsOf(run);
  const before = verdictsOf(base);
  const fields = Array.from(before.keys()).concat(Array.from(after.keys()).filter(field => !before.has(field)));
  const changes: TVerdictChange[] = fields
    .map(field => ({
      field,
      fieldLabel: after.get(field)?.fieldLabel || before.get(field)?.fieldLabel || field,
      before: before.get(field) || null,
      after: after.get(field) || null,
    }))
    .filter(change => !change.before || !change.after || change.before.status !== change.after.status);
  return { changes, sameCount: fields.length - changes.length };
};

const verdictText = (verdict: IFieldVerdict | null) =>
  verdict ? `${verdict.status}${typeof verdict.confidence === 'number' ? ` (${verdict.confidence}%)` : ''}` : 'not reviewed';

const runLabel = (run: IAiReviewRunListItem) =>
  [
    new Date(run.startedAt).toLocaleString('sv-SE'),
    run.model,
    run.promptVersion !== null ? `prompt v${run.promptVersion}` : null,
    describeRunOutcome(run),
    run.userName,
  ]
    .filter(Boolean)
    .join(' · ');

const VerdictChanges = ({ run, base }: { run: IAiReviewRun; base: IAiReviewRun }) => {
  const classes = useStyles();
  const { changes, sameCount } = compareVerdicts(run, base);
  return (
    <Box className={classes.section} data-testid="ai-review-run-changes">
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Changes against {new Date(base.startedAt).toLocaleString('sv-SE')} ({changes.length} changed, {sameCount} the same)
      </Typography>
      {changes.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Every field has the same status in both runs.
        </Typography>
      ) : (
        <ul className={classes.list}>
          {changes.map(change => (
            <li key={change.field}>
              <Typography variant="body2">
                <strong>{change.fieldLabel}</strong>: {verdictText(change.before)} → {verdictText(change.after)}
              </Typography>
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

const useRun = (id: string | null) =>
  useQuery<{ aiReviewRun: IAiReviewRun | null }>(AI_REVIEW_RUN_QUERY, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-first',
  });

interface IAiReviewRunDialogProps {
  runId: string;
  // Lists the other runs of the product to switch to or compare with.
  productId?: string;
  onClose: () => void;
}

// Superuser only: a stored AI review run, the other runs of the product, and how two of them differ.
const AiReviewRunDialog = ({ runId, productId, onClose }: IAiReviewRunDialogProps) => {
  const [selectedId, setSelectedId] = React.useState(runId);
  const [compareId, setCompareId] = React.useState('');
  const { data, loading, error } = useRun(selectedId);
  const compared = useRun(compareId || null);
  const runs = useQuery<{ aiReviewRuns: IAiReviewRunListItem[] }>(AI_REVIEW_RUNS_QUERY, {
    variables: { productId },
    skip: !productId,
    fetchPolicy: 'cache-and-network',
  });
  const run = data?.aiReviewRun;
  const base = compared.data?.aiReviewRun;
  const history = runs.data?.aiReviewRuns || [];

  const select = (id: string, label: string, value: string, onChange: (next: string) => void, options: IAiReviewRunListItem[], empty?: string) => (
    <TextField
      id={id}
      select
      fullWidth
      size="small"
      variant="outlined"
      label={label}
      value={value}
      onChange={event => onChange(event.target.value)}
      SelectProps={{ native: true }}
      // The empty choice has a label of its own, so keep the field label above it.
      InputLabelProps={{ shrink: true }}
    >
      {empty !== undefined && <option value="">{empty}</option>}
      {options.map(item => (
        <option key={item.id} value={item.id}>
          {runLabel(item)}
        </option>
      ))}
    </TextField>
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="ai-review-run-title">
      <DialogTitle id="ai-review-run-title">AI review details</DialogTitle>
      <DialogContent dividers>
        {history.length > 1 && (
          <Box display="grid" style={{ gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }} mb={3}>
            {select('ai-review-run-select', `Run (${history.length} in all)`, selectedId, next => {
              setSelectedId(next);
              if (next === compareId) setCompareId('');
            }, history)}
            {select(
              'ai-review-run-compare',
              'Compare with',
              compareId,
              setCompareId,
              history.filter(item => item.id !== selectedId),
              'No comparison'
            )}
          </Box>
        )}
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!loading && !error && !run && <Alert severity="info">The details of this review were not found.</Alert>}
        {run && base && <VerdictChanges run={run} base={base} />}
        {run && <AiReviewRunDetails run={run} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiReviewRunDialog;
